import fp from 'lodash/fp';
import React, {
    FC,
    FocusEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FieldRenderProps } from 'react-final-form';
import { ClickAwayListener, Fade, Popper, PopperProps, SxProps, Theme, formHelperTextClasses, inputAdornmentClasses, inputBaseClasses } from '@mui/material';
import { parseDate, getConvertedDate, formatDateTime, formatDate } from '@factorin/util';
import { forms } from '@factorin/container.macro';

import { CalendarTypes } from 'Modules/ui3/types';

import { Calendar24, MiniClose24 } from '../../icons';
import { Calendar2, CalendarProps2 } from '../Calendar2';
import { Input, InputProps } from '../Input';
import { Box } from '../Box';
import { Typography } from '../Typography';


const DATE_WITH_TIME_DIVIDER = ', ';
const DATE_MASK = 'Dd.Mm.Yyyy';


const usePopperOpenState = (onOpen?: () => void, onClose?: () => void) => {
    const [isOpen, setOpen] = useState(() => false);

    const change = useCallback(() => {
        if (onOpen && !isOpen)
            onOpen();
        if (onClose && isOpen)
            onClose();
        setOpen(!isOpen);
    }, [isOpen, onOpen, onClose]);

    const open = useCallback(() => {
        if (onOpen)
            onOpen();
        setOpen(true);
    }, [onOpen]);

    const close = useCallback(() => {
        setOpen(false);
        if (onClose)
            onClose();
    }, [onClose]);

    return {
        isOpen,
        change,
        open,
        close,
    };
};

const getIconConfig = (sx: SxProps<Theme>) => ({
    cursor: 'pointer',
    sx: {
        width: ({ spacing }) => spacing(6),
        height: ({ spacing }) => spacing(6),
        ...sx,
    },
});


export type CalendarInputProps = Omit<CalendarProps2, 'Buttons'> &
Omit<InputProps, 'value' | 'onChange' | 'ref'> & { popperProps?: PopperProps, noLabel?: boolean, needCloseSuffix?: boolean, intervalError?: boolean, isUTCConvert?: boolean, calendarType?: CalendarTypes, isFastPeriodButtonsExists?: boolean };


const CalendarInputForwardRef = React.forwardRef<
HTMLInputElement,
CalendarInputProps
>(({ locale = 'ru', value, onChange, onFocus, onBlur, popperProps, noLabel = false, needCloseSuffix = false, isUTCConvert = false, calendarType = 'onlyDate', isFastPeriodButtonsExists = false, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement>();

    const { isOpen, open, close } = usePopperOpenState();

    const [inputValue, setInputValue] = useState<string>('');

    const [formatChars, setFormatChars] = useState({
        D: '[0-3]',
        d: '[0-9]',
        M: '[0-1]',
        m: '[0-9]',
        Y: '[1-3]',
        y: '[0-9]',

        H: '[0-2]',
        h: '[0-9]',
        Q: '[0-5]',
        q: '[0-9]',
    });

    useEffect(() => {
        const currentValue = inputValue || formatDateTime(value, locale, DATE_WITH_TIME_DIVIDER);

        if (currentValue) {
            const timeUpdatedConfig = {};

            const [day, month, yearAndTime] = currentValue.split('.');

            if (calendarType === 'withTimePicker') {
                const [, time] = yearAndTime?.split(DATE_WITH_TIME_DIVIDER);
                const [hours] = time?.split(':') ?? [undefined];

                if (hours) {
                    Object.assign(timeUpdatedConfig, {
                        H: Number(hours[1]) > 4 ? '[0-1]' : '[0-2]',
                        h: hours[0] === '2' ? '[0-4]' : '[0-9]',
                    });
                }
            }

            setFormatChars(previousConfig => ({
                ...previousConfig,

                D: Number(day[1]) > 2 ? '[0-2]' : '[0-3]',
                d: day[0] === '3' ? '[0-1]' : '[0-9]',
                M: Number(month[1]) > 2 ? '0' : '[0-1]',
                m: month[0] === '1' ? '[0-2]' : '[0-9]',

                ...timeUpdatedConfig,
            }));
        }
    }, [setFormatChars, value, locale, calendarType, inputValue]);

    const commitInputChange = useMemo(
        () =>
            fp.throttle(200)((value: string) => {
                if (calendarType === 'withTimePicker') {
                    const [date, time] = value?.split(DATE_WITH_TIME_DIVIDER);

                    const parsed = parseDate(date, locale) ?? undefined;

                    const [hours, minutes] = time?.split(':') ?? [undefined, undefined];

                    if (hours && minutes) {
                        parsed?.setHours(Number(hours) || 0);
                        parsed?.setMinutes(Number(minutes) || 0);
                    }

                    return onChange(isUTCConvert ? getConvertedDate('withTime', parsed) : parsed);
                }

                const parsed = parseDate(value, locale) ?? undefined;

                return onChange(isUTCConvert ? getConvertedDate('UTCFormat', parsed) : parsed);
            }),
        [onChange, locale, isUTCConvert, calendarType],
    );

    const onInputChange = useCallback<(value: string) => void>(
        value => {
            setInputValue(value);
            commitInputChange(value);
        }, [commitInputChange]);

    const onInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
        e => {
            setInputValue('');

            if (onBlur)
                onBlur(e);
        },
        [onBlur, setInputValue],
    );

    const onCalendarChange = useCallback(
        (date: Date) => {
            onChange(isUTCConvert
                ? getConvertedDate(calendarType === 'withTimePicker'
                    ? 'withTime' : 'UTCFormat', date)
                : date);
            setInputValue('');
        },
        [onChange, isUTCConvert, setInputValue, calendarType],
    );

    const formattedDate = useMemo(() => calendarType === 'withTimePicker'
        ? formatDateTime(value, locale, DATE_WITH_TIME_DIVIDER)
        : formatDate(value, locale),
    [value, locale, calendarType]);

    const withoutDateButtonsCalendarYPosition = calendarType === 'withTimePicker' ? -18 : -12;

    return (
        <ClickAwayListener onClickAway={close}>
            <Box ref={containerRef}>
                <Input
                    ref={ref}
                    autoComplete='off'

                    mask={calendarType === 'withTimePicker' ? `${DATE_MASK}${DATE_WITH_TIME_DIVIDER}Hh:Qq` : DATE_MASK}
                    formatChars={formatChars}
                    maskChar='_'

                    value={inputValue || formattedDate}
                    onChange={onInputChange}
                    onBlur={onInputBlur}
                    onFocus={open}

                    size='medium'
                    suffix={needCloseSuffix && (inputValue || formattedDate) ? (
                        <MiniClose24
                            {...getIconConfig({ color: 'grayscale.shade100' })}
                            onClick={() => onChange(null)}
                        />
                    ) : (
                        <Calendar24
                            {...getIconConfig({ color: 'grayscale.shade85' })}
                            onClick={open}
                        />
                    )}
                    noLabel={noLabel}
                    InputProps={{
                        sx: ({ spacing }) => ({
                            [`& .${inputAdornmentClasses.root}`]: {
                                cursor: 'pointer',
                                fontSize: spacing(6),
                            },
                        }),
                    }}

                    {...isFastPeriodButtonsExists && {
                        specialHelperSx: {
                            [`& .${inputBaseClasses.root}+.${formHelperTextClasses.root}`]: {
                                marginTop: 12,
                                marginLeft: 0,
                            },
                        },
                    }}

                    {...props}
                />

                <Popper
                    open={isOpen}
                    anchorEl={containerRef.current}
                    sx={{
                        zIndex: 51,
                    }}
                    popperOptions={{
                        ...popperProps,
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [
                                        0,
                                        isFastPeriodButtonsExists
                                            ? -60
                                            : withoutDateButtonsCalendarYPosition,
                                    ],
                                },
                            },
                        ],
                    }}
                    placeholder={null}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps}>
                            <Box>
                                <Calendar2
                                    value={value}
                                    onChange={onCalendarChange}
                                    locale={locale}
                                    calendarType={calendarType}
                                />
                            </Box>
                        </Fade>
                    )}
                </Popper>
            </Box>
        </ClickAwayListener>
    );
});

export const CalendarInput = React.memo(CalendarInputForwardRef);


export const CalendarInputField: FC<FieldRenderProps<Date> & CalendarInputProps> = ({
    input: { name, value, onChange, onBlur, onFocus },
    meta: { modified, error, valid },
    helperText,
    onPaste,
    intervalError,
    ...props
}) => (
    <CalendarInput
        name={name}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        helperText={
            modified && !intervalError && (error || !valid) ? (
                <Typography sx={{ color: 'infoCustom.red50' }} variant='regularCaption3'>
                    <forms.errorRenderer error={error} />
                </Typography>
            ) : helperText
        }
        error={modified && (!!error || !valid) || intervalError}
        {...props}
    />
);
